import { useCallback, useMemo } from 'react';

import { type RequestPolicy } from 'urql';

import { useAppState, useFunctionPolling, useIsFocused } from '@sg/garnish';
import { OrderType } from '@sg/graphql-schema';

import { getOrderTimeRange, ignoreTimezone } from '@order/utils';

import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  checkIfExpiredOrder,
  checkIfLateOrder,
  checkIfPastOrder,
  checkIfPendingOrder,
  getOrderTime,
} from '../../context/OrderStatus';
import {
  useLastOrderStatusQuery,
  type LastOrderStatusQuery,
} from './LastOrderStatus.query.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useLastOrderStatus = (params: Params) => {
  const { requestPolicy, withPolling = false } = params;

  const isScreenFocused = useIsFocused();
  const isLoggedIn = useIsLoggedIn();
  const appState = useAppState({ pause: !withPolling });

  // ─── Order Status Query ───────────────────────────────────────────────────

  const [response, refetchLastOrderStatus] = useLastOrderStatusQuery({
    requestPolicy,
    pause: !isScreenFocused || !isLoggedIn,
  });

  const { data: status, fetching: isLoadingLastOrder } = response;

  // ─── Derived ───────────────────────────────────────────────────────────────

  const lastOrder = useMemo(() => {
    if (!isOrderStatus(status?.orderStatus)) {
      return {
        orderId: null,
        orderType: OrderType.Pickup,
        orderStatus: null,
        orderTime: null,
        hasEarnedLoyaltyPoints: false,
        isRecent: false,
        isLate: false,
        isPendingFeedback: false,
        isTrackable: false,
        isLoadingLastOrder,
        restaurantEntityId: '',
        lineItems: [],
      };
    }

    const lastOrder = status.orderStatus;

    // ─── Recent ─────────────────────────────────────────────────────────────

    const isRecent = !checkIfExpiredOrder({
      order: lastOrder.order,
      hoursInThePast: 3,
    });

    // ─── Late ───────────────────────────────────────────────────────────────

    const isLate = checkIfLateOrder(lastOrder);

    // ─── Pending Feedback ───────────────────────────────────────────────────

    const isLastOrderCompleted = checkIfPastOrder({
      order: lastOrder.order,
      flattenedOrderStatus: lastOrder.flattenedOrderStatus,
    });
    const isLastOrderExpired = checkIfExpiredOrder(lastOrder);
    const isRated = !lastOrder.order.isPendingFeedback;
    const isPendingFeedback = !isLastOrderExpired && isLastOrderCompleted && !isRated;

    // ─── Trackable ──────────────────────────────────────────────────────────

    const isTrackable = Boolean(lastOrder.order.canTrackOrderStatus);

    // ─── Last Order Info ────────────────────────────────────────────────────

    const orderId = lastOrder.order.id;
    const { orderType, hasEarnedLoyaltyPoints } = lastOrder.order;
    const orderStatus = lastOrder.flattenedOrderStatus;
    const timeWithTimezone = getOrderTime(lastOrder.order);
    const orderTime = timeWithTimezone ? ignoreTimezone(timeWithTimezone) : null;
    const { startTime = '', endTime = '' } = orderTime ? getOrderTimeRange(orderTime) : {};
    const restaurantEntityId = String(lastOrder.order.restaurant?.entityId ?? '');
    const lineItems = lastOrder.order.lineItems;

    return {
      orderId,
      orderType,
      orderStatus,
      orderTime,
      startTime,
      endTime,
      hasEarnedLoyaltyPoints,
      isRecent,
      isLate,
      isPendingFeedback,
      isRated,
      isTrackable,
      isLoadingLastOrder,
      restaurantEntityId,
      lineItems,
    };
  }, [isLoadingLastOrder, status]);

  // ───────────────────────────────────────────────────────────────────────────

  const isAppActive = appState === 'active';
  const hasPendingOrder = lastOrder.orderStatus
    ? checkIfPendingOrder(lastOrder.orderStatus)
    : false;

  const shouldSkipPolling =
    !withPolling || !isScreenFocused || !isAppActive || !isLoggedIn || !hasPendingOrder;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handlePoll = useCallback(() => {
    refetchLastOrderStatus({ requestPolicy: 'network-only' });
  }, [refetchLastOrderStatus]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useFunctionPolling(POLLING_INTERVAL, handlePoll, shouldSkipPolling);

  // ───────────────────────────────────────────────────────────────────────────

  return lastOrder;
};

// ─── Guards ──────────────────────────────────────────────────────────────────

const isOrderStatus = (
  orderStatus: LastOrderStatusQuery['orderStatus'] | undefined,
): orderStatus is Extract<LastOrderStatusQuery['orderStatus'], { __typename: 'OrderStatus' }> =>
  orderStatus != null && '__typename' in orderStatus && orderStatus.__typename === 'OrderStatus';

// ─── Constants ───────────────────────────────────────────────────────────────

const POLLING_INTERVAL = 1000 * 60;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  requestPolicy: RequestPolicy;
  withPolling?: boolean;
};
